import Dragger from "antd/lib/upload/Dragger";
import React, { useState } from "react";
import Flex from "../../../../../components/Flex";
import {
  CloseCircleOutlined,
  FileOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Typography, UploadFile, UploadProps } from "antd";
import ConfirmationModal from "../../../../../components/ConfirmationModal";

type FileDropzoneProps = {
  onChange: (fileList: UploadFile[]) => void;
  fileList?: UploadFile[];
  allowedFileExtensions?: string[];
  deleteFile: (file: UploadFile) => void;
  disabled?: boolean;
};

export default function FileDropzone({
  onChange,
  allowedFileExtensions,
  deleteFile,
  fileList,
  disabled,
}: FileDropzoneProps) {
  const [deleteConfirmationModalPayload, setDeleteConfirmationModalPayload] =
    useState<UploadFile<any> | null>(null);
  const [showHelperText, setShowHelperText] = useState<boolean>(false);
  const draggerProps: UploadProps = {
    multiple: true,
    action: "",
    fileList,
    // Disable implicit POST request to action URL.
    beforeUpload(file, fileList) {
      return Promise.reject();
    },
    onChange(info) {
      let containsInvalidFile = false;
      const allowedFiles = info.fileList.filter((file) => {
        if (!allowedFileExtensions || allowedFileExtensions.length === 0)
          return true;
        const fileExtension = file.name.split(".").pop() ?? "";
        if (!fileExtension) return false;
        const fileIsAllowed = allowedFileExtensions.includes(fileExtension);
        if (!fileIsAllowed) {
          containsInvalidFile = true;
        }
        return fileIsAllowed;
      });

      if (containsInvalidFile) {
        setShowHelperText(true);
      } else {
        setShowHelperText(false);
      }
      onChange?.(allowedFiles);
    },
  };
  return (
    <Flex vertical gap="0.6rem" style={{ width: "100%" }}>
      <Dragger
        {...draggerProps}
        style={{
          background: "none",
          borderStyle: "solid",
          borderRadius: "8px",
          width: "100%",
        }}
        showUploadList={false}
        height={220}
        disabled={disabled}
      >
        <Flex vertical align="center" justify="center" gap="10px">
          <Flex vertical align="center" gap="5px">
            <UploadOutlined style={{ fontSize: "42px" }} />
            <Typography>Drag and drop or</Typography>
          </Flex>
          <Button
            style={{
              height: "37px",
              width: "100px",
              borderRadius: "8px",
            }}
            type="primary"
            onClick={() => {}}
            disabled={disabled}
          >
            Browse
          </Button>
          {!!allowedFileExtensions?.length && (
            <Typography
              style={{
                fontSize: "0.7rem",
                opacity: 0.7,
                ...(showHelperText
                  ? { color: "#9b4553", opacity: 1, fontWeight: 700 }
                  : {}),
              }}
            >
              {`${
                showHelperText ? "This uploader only supports " : ""
              }${allowedFileExtensions
                .map((ext) => ext.toUpperCase())
                .join(", ")}`}
            </Typography>
          )}
        </Flex>
      </Dragger>
      {!!fileList?.length && (
        <>
          <Flex vertical gap="8px">
            {fileList?.map((file) => (
              <Flex
                justify="space-between"
                align="center"
                key={file.uid}
                style={{
                  width: "100%",
                  background: "rgba(204, 217, 241, 0.15)",
                  padding: "8px",
                  border: "1px dashed rgba(204, 217, 241, 1)",
                  borderRadius: "4px",
                }}
              >
                <Flex align="center" gap="10px">
                  <FileOutlined style={{ fontSize: "24px" }} />
                  <Typography>{file.name}</Typography>
                </Flex>
                <Button
                  type="text"
                  disabled={disabled}
                  icon={
                    <CloseCircleOutlined
                      style={{
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                  }
                  style={{ height: "24px", width: "24px" }}
                  onClick={() => {
                    setDeleteConfirmationModalPayload(file);
                  }}
                />
              </Flex>
            ))}
          </Flex>
          <ConfirmationModal
            modalIsOpenState={[
              !!deleteConfirmationModalPayload,
              (modalIsOpen) => setDeleteConfirmationModalPayload(null),
            ]}
            headerText="Delete file?"
            bodyText="You can always reupload it later."
            proceedButtonText="Delete the file"
            proceedButtonColor="danger"
            cancelButtonText="Cancel"
            onProceed={() => {
              if (!!deleteConfirmationModalPayload) {
                deleteFile(deleteConfirmationModalPayload);
              }
              setDeleteConfirmationModalPayload(null);
            }}
          />
        </>
      )}
    </Flex>
  );
}
